import React, { memo, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Spinner } from 'reactstrap';

import AppBadge from 'components/App/AppBadge';
import flightBookingApi from 'api/flightBookingApi';
import { formatFlightAmount, handleFlightApiError } from 'constants/flightBooking';
import { formatNumber } from 'utils/currencyFormat';
// The exact "bordered/shadowed tile, label, big value, sub-line" look ManagerFlightRevenue
// already built (its SUMMARY_CARDS), reused as-is rather than a second version of the same card.
import revenueStyles from 'pages/ManagerFlightRevenue/ManagerFlightRevenue.module.scss';

import { NEEDS_ACTION_LINK, getDashboardReasonRows } from './flightDashboard';

import styles from './FlightOverview.module.scss';

// GET /flight-dashboard (api-contract.md): FLIGHT_BOOKING module, intranet only. The caller
// (Home.jsx) only mounts this when the account has that module, so the request itself never
// fires for an account without it — no separate "hidden" check needed in here.
const EMPTY_DASHBOARD = {
  currency: 'VND',
  today: {
    issuedCount: 0, ticketCount: 0, serviceFee: 0, refundedTotal: 0,
  },
  monthToDate: {
    issuedCount: 0, ticketCount: 0, serviceFee: 0, refundedTotal: 0, serviceFeeRefunded: 0, net: 0,
  },
  needsAction: { total: 0, byReason: {} },
  open: { waitingPayment: 0, issuing: 0 },
};

function FlightOverview() {
  const [dashboard, setDashboard] = useState(EMPTY_DASHBOARD);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let ignore = false;

    flightBookingApi.getFlightDashboard()
      .then((response) => {
        if (ignore) return;
        setDashboard({ ...EMPTY_DASHBOARD, ...response.payload });
      })
      .catch((error) => {
        if (!ignore) handleFlightApiError(error);
      })
      .finally(() => !ignore && setIsLoading(false));

    return () => { ignore = true; };
  }, []);

  const {
    currency, today, monthToDate, needsAction, open,
  } = dashboard;
  const reasonRows = getDashboardReasonRows(needsAction.byReason);
  const hasNeedsAction = needsAction.total > 0;

  return (
    <div className={styles.FlightOverview}>
      <span className={styles.Title}>Vé máy bay</span>

      {isLoading ? (
        <div className={styles.Loading}>
          <Spinner size="sm" color="primary" />
          {' '}
          Đang tải dữ liệu...
        </div>
      ) : (
        <>
          <div className={revenueStyles.Cards}>
            <Card className={revenueStyles.SummaryCard}>
              <div className={revenueStyles.CardLabel}>Vé xuất hôm nay</div>
              <div className={revenueStyles.CardValue}>{formatNumber(today.ticketCount)}</div>
              <div className={revenueStyles.CardNote}>{`${formatNumber(today.issuedCount)} đơn`}</div>
            </Card>

            <Card className={revenueStyles.SummaryCard}>
              <div className={revenueStyles.CardLabel}>Phí dịch vụ hôm nay</div>
              <div className={revenueStyles.CardValue}>
                {formatFlightAmount(today.serviceFee, currency)}
              </div>
            </Card>

            <Card className={revenueStyles.SummaryCard}>
              <div className={revenueStyles.CardLabel}>Doanh thu ròng tháng này</div>
              <div className={revenueStyles.CardValue}>
                {formatFlightAmount(monthToDate.net, currency)}
              </div>
            </Card>

            <Link
              to={NEEDS_ACTION_LINK}
              className={`${styles.NeedsActionLink} ${hasNeedsAction ? styles.NeedsActionLinkDanger : ''}`}
            >
              <Card className={`${revenueStyles.SummaryCard} ${styles.NeedsActionCard}`}>
                <div className={revenueStyles.CardLabel}>Cần xử lý</div>
                <div className={`${revenueStyles.CardValue} ${styles.NeedsActionValue}`}>
                  {formatNumber(needsAction.total)}
                  <span className={styles.NeedsActionArrow}>→</span>
                </div>
              </Card>
            </Link>
          </div>

          {reasonRows.length > 0 && (
            <div className={styles.ReasonList}>
              {reasonRows.map((row) => (
                <Link to={row.href} key={row.reason} className={styles.ReasonRow}>
                  <span>{row.label}</span>
                  <AppBadge color="error">{formatNumber(row.count)}</AppBadge>
                </Link>
              ))}
            </div>
          )}

          <div className={styles.OpenCounts}>
            <span className={styles.OpenChip}>{`Chờ thanh toán ${formatNumber(open.waitingPayment)}`}</span>
            <span className={styles.OpenChip}>{`Đang xuất vé ${formatNumber(open.issuing)}`}</span>
          </div>
        </>
      )}
    </div>
  );
}

export default memo(FlightOverview);
